<!-- src/components/Applications/BreytingSereign/Steps/Step2.vue -->
<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import { computed, ref, watch } from 'vue'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'

const { t } = useI18n()
const { umsokn } = useUmsokn()

const props = defineProps<{
  disable?: boolean
}>()

// Select options (match legacy)
const framlagPercent = ['2%', '3%', '4%']

// Investment path picker
interface InvestmentPath {
  key: string
  letter: string
  name: string
  description?: string
  hlutabref?: string
  skuldabref?: string
  innlan?: string
}

const investmentPaths = computed<InvestmentPath[]>(() => [
  {
    key: 'aldurstengd',
    letter: 'A',
    name: t('sereignar_umsokn_step2_fjarfestingarleid_aldurstengd'),
    description: t('sereignar_umsokn_step2_aldurstengd_lysing'),
  },
  {
    key: 'eign7030',
    letter: 'B',
    name: t('sereignar_umsokn_step2_fjarfestingarleid_eign7030'),
    hlutabref: t('sereignar_umsokn_step2_eign7030_hlutabref'),
    skuldabref: t('sereignar_umsokn_step2_eign7030_skuldabref'),
  },
  {
    key: 'eign5050',
    letter: 'C',
    name: t('sereignar_umsokn_step2_fjarfestingarleid_eign5050'),
    hlutabref: t('sereignar_umsokn_step2_eign5050_hlutabref'),
    skuldabref: t('sereignar_umsokn_step2_eign5050_skuldabref'),
  },
  {
    key: 'eign2080',
    letter: 'D',
    name: t('sereignar_umsokn_step2_fjarfestingarleid_eign2080'),
    hlutabref: t('sereignar_umsokn_step2_eign2080_hlutabref'),
    skuldabref: t('sereignar_umsokn_step2_eign2080_skuldabref'),
  },
  {
    key: 'verdtryggd',
    letter: 'E',
    name: t('sereignar_umsokn_step2_fjarfestingarleid_verdtryggd'),
    innlan: t('sereignar_umsokn_step2_verdtryggd_innlan'),
  },
  {
    key: 'erlend',
    letter: 'F',
    name: t('sereignar_umsokn_step2_fjarfestingarleid_erlend'),
    hlutabref: t('sereignar_umsokn_step2_erlend_hlutabref'),
    skuldabref: t('sereignar_umsokn_step2_erlend_skuldabref'),
  },
])

const validPathKeys = ['aldurstengd', 'eign7030', 'eign5050', 'eign2080', 'verdtryggd', 'erlend']

const selectedPath = computed(() =>
  investmentPaths.value.find(p => p.key === umsokn.value?.framtidarSyn)
)
const selectedFromPath = computed(() =>
  investmentPaths.value.find(p => p.key === umsokn.value?.framtidarSynFrom)
)
const selectedToPath = computed(() =>
  investmentPaths.value.find(p => p.key === umsokn.value?.framtidarSynTo)
)

// Dropdown items for inneign flutt selects
const investmentPathItems = computed(() =>
  investmentPaths.value.map(p => ({ title: p.name, value: p.key }))
)

// Filtered dropdown: "from" excludes the selected "to" path
const fromPathItems = computed(() =>
  investmentPathItems.value.filter(item => item.value !== umsokn.value?.framtidarSynTo)
)

// Filtered dropdown: "to" excludes the selected "from" path
const toPathItems = computed(() =>
  investmentPathItems.value.filter(item => item.value !== umsokn.value?.framtidarSynFrom)
)

function selectPath(path: InvestmentPath) {
  if (props.disable) return
  umsokn.value.framtidarSyn = path.key
}

function selectFromPath(path: InvestmentPath) {
  if (props.disable) return
  if (umsokn.value.framtidarSynFrom === path.key) {
    umsokn.value.framtidarSynFrom = null
  } else {
    umsokn.value.framtidarSynFrom = path.key
    if (umsokn.value.framtidarSynTo === path.key) {
      umsokn.value.framtidarSynTo = null
    }
  }
}

function selectToPath(path: InvestmentPath) {
  if (props.disable) return
  if (umsokn.value.framtidarSynTo === path.key) {
    umsokn.value.framtidarSynTo = null
  } else {
    umsokn.value.framtidarSynTo = path.key
    if (umsokn.value.framtidarSynFrom === path.key) {
      umsokn.value.framtidarSynFrom = null
    }
  }
}



function isSelected(path: InvestmentPath) {
  return umsokn.value?.framtidarSyn === path.key
}

function isFromSelected(path: InvestmentPath) {
  return umsokn.value?.framtidarSynFrom === path.key
}

function isToSelected(path: InvestmentPath) {
  return umsokn.value?.framtidarSynTo === path.key
}

// Reset helpers (mirror legacy on toggle)
watch(() => umsokn.value?.breytingFramlag, (on) => {
  if (!on && umsokn.value) {
    umsokn.value.framlagFrom = null
    umsokn.value.framlagTo = null
    umsokn.value.launagreidandiNafnKt = null
    umsokn.value.launagreidandiNetfang = null
  }
})
watch(() => umsokn.value?.avoxtunarLeid, (on) => {
  if (!on && umsokn.value) {
    umsokn.value.framtidarSyn = null
  } else if (on && umsokn.value && !validPathKeys.includes(umsokn.value.framtidarSyn)) {
    umsokn.value.framtidarSyn = investmentPaths.value[0]?.key
  }
})
watch(() => umsokn.value?.inneignFlutt, (on) => {
  if (!on && umsokn.value) {
    umsokn.value.framtidarSynFrom = null
    umsokn.value.framtidarSynTo = null
    umsokn.value.inneignFluttAthugasemdir = null
  }
})
watch(() => umsokn.value?.moveInneign, (on) => {
  if (!on && umsokn.value) {
    umsokn.value.placementContact = null
  }
})

// Manual error tracking (Vuelidate as backup for field-level display)
const showErrors = ref(false)

function atLeastOneSelected() {
  const u = umsokn.value
  return !!(
    u?.breytingFramlag ||
    u?.avoxtunarLeid ||
    u?.inneignFlutt ||
    u?.moveInneign
  )
}

function validateManually(): boolean {
  const u = umsokn.value
  if (!u) return false

  if (!atLeastOneSelected()) return false

  if (u.breytingFramlag) {
    if (!u.framlagFrom || !u.framlagTo) return false
  }
  if (u.avoxtunarLeid) {
    if (!u.framtidarSyn) return false
  }
  if (u.inneignFlutt) {
    if (!u.framtidarSynFrom || !u.framtidarSynTo) return false
  }
  if (u.moveInneign) {
    if (!u.placementContact) return false
  }

  return true
}

// Vuelidate rules (conditional requireds)
const rules = {
  umsokn: {
    framlagFrom: { required: requiredIf(() => !!umsokn.value?.breytingFramlag) },
    framlagTo:   { required: requiredIf(() => !!umsokn.value?.breytingFramlag) },

    framtidarSyn:       { required: requiredIf(() => !!umsokn.value?.avoxtunarLeid) },

    framtidarSynFrom:   { required: requiredIf(() => !!umsokn.value?.inneignFlutt) },
    framtidarSynTo:     { required: requiredIf(() => !!umsokn.value?.inneignFlutt) },

    placementContact:   { required: requiredIf(() => !!umsokn.value?.moveInneign) },
  }
}

const v$ = useVuelidate(rules, { umsokn })

// Expose validate() so ApplicationBase can advance
defineExpose({
  validate() {
    showErrors.value = true
    v$.value.$touch()
    return validateManually()
  }
})
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <v-row>
        <v-col>
          <div class="forms__header">
            {{ t('umsokn_sereignbreyting_step2_header') }}
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <p style="font-weight:bold">
            {{ t('umsokn_sereignbreyting_step2_header2') }}
          </p>
        </v-col>
      </v-row>

      <!-- Breyting á framlagi (combined from hækkun + lækkun) -->
      <v-row>
        <v-col>
          <v-checkbox
            v-model="umsokn.breytingFramlag"
            :readonly="props.disable"
            :label="t('umsokn_sereignbreyting_step2_breytingframlag')"
          />
        </v-col>
      </v-row>

      <v-expand-transition>
        <div v-if="umsokn.breytingFramlag">
          <v-row>
            <v-col cols="12" md="4" class="md-offset-1">
              <v-select
                v-model="umsokn.framlagFrom"
                :items="framlagPercent"
                :label="t('umsokn_sereignbreyting_step2_framlagur')"
                variant="outlined"
                :readonly="props.disable"
                :error="showErrors && umsokn.breytingFramlag && !umsokn.framlagFrom"
                :error-messages="
                  showErrors && umsokn.breytingFramlag && !umsokn.framlagFrom
                    ? [t('umsokn_sereignbreyting_step2_framlagur_error')]
                    : []
                "
                menu-icon="mdi-menu-down"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="umsokn.framlagTo"
                :items="framlagPercent"
                :label="t('umsokn_sereignbreyting_step2_framlagtil')"
                variant="outlined"
                :readonly="props.disable"
                :error="showErrors && umsokn.breytingFramlag && !umsokn.framlagTo"
                :error-messages="
                  showErrors && umsokn.breytingFramlag && !umsokn.framlagTo
                    ? [t('umsokn_sereignbreyting_step2_framlagtil_error')]
                    : []
                "
                menu-icon="mdi-menu-down"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4" class="md-offset-1">
              <v-text-field
                v-model="umsokn.launagreidandiNafnKt"
                :label="t('umsokn_sereignbreyting_step2_launagreidandi_nafn_kt')"
                :readonly="props.disable"
                variant="outlined"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="umsokn.launagreidandiNetfang"
                :label="t('umsokn_sereignbreyting_step2_launagreidandi_netfang')"
                :readonly="props.disable"
                variant="outlined"
              />
            </v-col>
          </v-row>
        </div>
      </v-expand-transition>

      <!-- Ávöxtunarleið -->
      <v-row>
        <v-col>
          <v-checkbox
            v-model="umsokn.avoxtunarLeid"
            :readonly="props.disable"
            :label="t('umsokn_sereignbreyting_step2_avoxtun')"
          />
        </v-col>
      </v-row>

      <v-expand-transition>
        <div v-if="umsokn.avoxtunarLeid">
          <h3 class="path-section__title">{{ t('sereignar_umsokn_step2_veldu_fjarfestingarleid') }}</h3>
          <p class="path-section__desc">
            {{ t('umsokn_tilgreind_sereign_step3_fjarfestingarleid_intro') }}
          </p>
          <p class="path-section__desc path-section__desc--link">
            {{ t('umsokn_tilgreind_sereign_step3_lykilupplysingar_texti') }}
            <a class="link" target="_blank" :href="t('sereignar_umsokn_lykilupplysingar_url')">{{ t('umsokn_tilgreind_sereign_step3_lykilupplysingar_link') }}</a>
          </p>

          <p v-if="showErrors && umsokn.avoxtunarLeid && !umsokn.framtidarSyn" class="path-section__error">
            {{ t('umsokn_sereignbreyting_step2_avoxtun_error') }}
          </p>

          <!-- Summary mode -->
          <template v-if="props.disable && selectedPath">
            <div class="path-cards">
              <div class="path-card path-card--selected">
                <div class="path-card__badge">
                  <span class="path-card__letter path-card__letter--selected"></span>
                </div>
                <div class="path-card__name-col">
                  <span class="path-card__name">{{ selectedPath.name }}</span>
                </div>
                <div class="path-card__middle">
                  <span v-if="selectedPath.description" class="path-card__description">{{ selectedPath.description }}</span>
                  <div v-else-if="selectedPath.hlutabref || selectedPath.innlan" class="path-card__allocations">
                    <span v-if="selectedPath.hlutabref" class="path-card__alloc">
                      <span class="path-card__alloc-label">{{ t('sereignar_umsokn_step2_hlutabref') }}</span>
                      <span class="path-card__alloc-value">{{ selectedPath.hlutabref }}</span>
                    </span>
                    <span v-if="selectedPath.skuldabref" class="path-card__alloc">
                      <span class="path-card__alloc-label">{{ t('sereignar_umsokn_step2_skuldabref') }}</span>
                      <span class="path-card__alloc-value">{{ selectedPath.skuldabref }}</span>
                    </span>
                    <span v-if="selectedPath.innlan" class="path-card__alloc">
                      <span class="path-card__alloc-label">{{ t('sereignar_umsokn_step2_innlan') }}</span>
                      <span class="path-card__alloc-value">{{ selectedPath.innlan }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Edit mode -->
          <template v-else>
            <div class="path-cards">
              <div
                v-for="path in investmentPaths"
                :key="path.key"
                class="path-card"
                :class="{ 'path-card--selected': isSelected(path) }"
              >
                <div class="path-card__badge">
                  <span class="path-card__letter" :class="{ 'path-card__letter--selected': isSelected(path) }"></span>
                </div>
                <div class="path-card__name-col">
                  <span class="path-card__name">{{ path.name }}</span>
                </div>
                <div class="path-card__middle">
                  <span v-if="path.description" class="path-card__description">{{ path.description }}</span>
                  <div v-else-if="path.hlutabref || path.innlan" class="path-card__allocations">
                    <span v-if="path.hlutabref" class="path-card__alloc">
                      <span class="path-card__alloc-label">{{ t('sereignar_umsokn_step2_hlutabref') }}</span>
                      <span class="path-card__alloc-value">{{ path.hlutabref }}</span>
                    </span>
                    <span v-if="path.skuldabref" class="path-card__alloc">
                      <span class="path-card__alloc-label">{{ t('sereignar_umsokn_step2_skuldabref') }}</span>
                      <span class="path-card__alloc-value">{{ path.skuldabref }}</span>
                    </span>
                    <span v-if="path.innlan" class="path-card__alloc">
                      <span class="path-card__alloc-label">{{ t('sereignar_umsokn_step2_innlan') }}</span>
                      <span class="path-card__alloc-value">{{ path.innlan }}</span>
                    </span>
                  </div>
                </div>
                <div class="path-card__action">
                  <button
                    class="path-card__btn"
                    :class="{ 'path-card__btn--active': isSelected(path) }"
                    :disabled="props.disable"
                    @click="selectPath(path)"
                  >
                    {{ isSelected(path) ? t('sereignar_umsokn_step2_valin_leid') : t('sereignar_umsokn_step2_velja_leid') }}
                  </button>
                </div>
              </div>
            </div>
          </template>
        </div>
      </v-expand-transition>

      <!-- Inneign flutt -->
      <v-row>
        <v-col>
          <v-checkbox
            v-model="umsokn.inneignFlutt"
            :readonly="props.disable"
            :label="t('umsokn_sereignbreyting_step2_inneign_flutt')"
          />
        </v-col>
      </v-row>

      <v-expand-transition>
        <div v-if="umsokn.inneignFlutt">
          <h3 class="path-section__title">{{ t('umsokn_sereignbreyting_step2_inneign_flutt_header') }}</h3>
          <p class="path-section__desc">
            {{ t('umsokn_tilgreind_sereign_step3_fjarfestingarleid_intro') }}
          </p>
          <p class="path-section__desc path-section__desc--link">
            {{ t('umsokn_tilgreind_sereign_step3_lykilupplysingar_texti') }}
            <a class="link" target="_blank" :href="t('sereignar_umsokn_lykilupplysingar_url')">{{ t('umsokn_tilgreind_sereign_step3_lykilupplysingar_link') }}</a>
          </p>

          <p v-if="showErrors && umsokn.inneignFlutt && (!umsokn.framtidarSynFrom || !umsokn.framtidarSynTo)" class="path-section__error">
            {{ t('umsokn_sereignbreyting_step2_inneign_flutt_error') }}
          </p>

          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="umsokn.framtidarSynFrom"
                :items="fromPathItems"
                :label="t('umsokn_sereignbreyting_step2_inneign_flutt_fra')"
                variant="outlined"
                :readonly="props.disable"
                :error="showErrors && umsokn.inneignFlutt && !umsokn.framtidarSynFrom"
                :error-messages="
                  showErrors && umsokn.inneignFlutt && !umsokn.framtidarSynFrom
                    ? [t('umsokn_sereignbreyting_step2_inneign_flutt_error')]
                    : []
                "
                menu-icon="mdi-menu-down"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="umsokn.framtidarSynTo"
                :items="toPathItems"
                :label="t('umsokn_sereignbreyting_step2_inneign_flutt_til')"
                variant="outlined"
                :readonly="props.disable"
                :error="showErrors && umsokn.inneignFlutt && !umsokn.framtidarSynTo"
                :error-messages="
                  showErrors && umsokn.inneignFlutt && !umsokn.framtidarSynTo
                    ? [t('umsokn_sereignbreyting_step2_inneign_flutt_error')]
                    : []
                "
                menu-icon="mdi-menu-down"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <p class="path-section__desc">
                {{ t('umsokn_sereignbreyting_step2_inneign_flutt_athugasemdir_lysing') }}
              </p>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="umsokn.inneignFluttAthugasemdir"
                :label="t('umsokn_sereignbreyting_step2_inneign_flutt_athugasemdir')"
                :readonly="props.disable"
                variant="outlined"
                rows="3"
                auto-grow
              />
            </v-col>
          </v-row>
        </div>
      </v-expand-transition>

      <!-- Óska eftir – vörsluaðili -->
      <v-row>
        <v-col>
          <v-checkbox
            v-model="umsokn.moveInneign"
            :readonly="props.disable"
            :label="t('umsokn_sereignbreyting_step2_oska_eftir')"
          />
        </v-col>
      </v-row>

      <v-expand-transition>
        <v-row v-if="umsokn.moveInneign">
          <v-col cols="12">
            <v-text-field
              v-model="umsokn.placementContact"
              :label="t('umsokn_sereignbreyting_step2_vorsluadili')"
              :readonly="props.disable"
              variant="outlined"
              :error="showErrors && umsokn.moveInneign && !umsokn.placementContact"
              :error-messages="
                showErrors && umsokn.moveInneign && !umsokn.placementContact
                  ? [t('umsokn_sereignbreyting_step2_vorsluadili_error')]
                  : []
              "
            />
          </v-col>
        </v-row>
      </v-expand-transition>

      <p v-if="showErrors && !atLeastOneSelected()" class="error__text">
        {{ t('umsokn_general_required_error') }}
      </p>
    </v-col>
  </div>
</template>

<style scoped lang="scss">
@use "@/scss/settings/variables" as *;

.m-b-40 { margin-bottom: 40px; }
.forms__header {
  color: #194458;
  font-weight: 900;
  font-size: 28px;
  line-height: 1.25;
  margin: 0 0 12px;
}

/* ── Path section ── */
.link {
  color: $color-primary;
  text-decoration: none;
  &:hover { text-decoration: underline; }
}

.path-section__title {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 8px;
  color: $color-text;
}

.path-section__desc {
  font-size: 14px;
  line-height: 1.6;
  color: $color-text-muted;
  margin-bottom: 4px;

  &--link { margin-bottom: 20px; }
}

/* ── Transfer header with mode switcher ── */
.transfer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
}

.mode-switcher {
  display: flex;
  border: 1.5px solid $color-border;
  border-radius: 8px;
  overflow: hidden;
}

.mode-switcher__btn {
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  background: transparent;
  color: $color-text-muted;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover:not(.mode-switcher__btn--active) { background-color: rgba($color-primary, 0.05); }
  &--active { color: #fff; }
  &--from.mode-switcher__btn--active { background-color: #e57373; }
  &--to.mode-switcher__btn--active { background-color: $color-primary; }
}

.path-section__error {
  color: #d32f2f;
  font-size: 14px;
  margin-bottom: 12px;
}

.path-cards {
  display: flex;
  flex-direction: column;
}

.path-card {
  display: grid;
  grid-template-columns: 48px 180px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 20px 0;
  border-bottom: 1px solid $color-border;
  transition: background-color 0.15s ease;

  &:first-child { border-top: 1px solid $color-border; }
  &:hover { background-color: rgba($color-primary, 0.05); }
  &--selected {
    background-color: rgba($color-primary, 0.10);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10), 0 0 0 1px rgba($color-primary, 0.25);
    border-radius: 8px;
    padding: 20px 12px;
  }
}

.path-card__badge { display: flex; justify-content: center; }

.path-card__letter {
  display: flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; min-width: 40px;
  border-radius: 50%;
  background-color: $color-secondary; color: #fff;
  font-weight: 700; font-size: 16px;
  transition: background-color 0.15s ease;
  &--selected { background-color: $color-primary; }
}

.path-card__name-col { display: flex; align-items: center; }
.path-card__name { font-weight: 700; font-size: 16px; color: $color-secondary; }

.path-card__middle { display: flex; align-items: center; min-height: 48px; }
.path-card__description { font-size: 14px; color: $color-text-muted; line-height: 1.5; font-style: italic; }
.path-card__allocations { display: flex; gap: 32px; }
.path-card__alloc { display: flex; flex-direction: column; align-items: flex-start; flex: 1; min-width: 72px; }
.path-card__alloc-label { font-size: 11px; color: $color-text-muted; text-transform: uppercase; letter-spacing: 0.3px; line-height: 1.3; }
.path-card__alloc-value { font-size: 17px; font-weight: 700; color: $color-primary; }

.path-card__action { display: flex; justify-content: flex-end; }
.path-card__transfer-btns { display: flex; gap: 6px; justify-content: flex-end; }
.path-card__btn {
  white-space: nowrap; padding: 10px 28px; font-size: 15px; font-weight: 600;
  border: 1.5px solid $color-primary; border-radius: 6px;
  background: transparent; color: $color-primary; cursor: pointer;
  transition: all 0.15s ease; min-width: 120px; text-align: center;
  &:hover:not(:disabled) { background-color: $color-primary; color: #fff; }
  &--active { background-color: $color-primary; color: #fff; pointer-events: none; }
  &--sm { padding: 6px 16px; font-size: 13px; min-width: 80px; }
  &--from { border-color: #e57373; background-color: #e57373; color: #fff; &:hover:not(:disabled) { background-color: #d32f2f; border-color: #d32f2f; } }
  &--to { border-color: $color-primary; background-color: $color-primary; color: #fff; pointer-events: none; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

/* ── Transfer (from/to) cards ── */
.path-card--transfer-from {
  background-color: rgba(#e57373, 0.08);
  box-shadow: 0 0 0 1px rgba(#e57373, 0.35);
  border-radius: 8px;
  padding: 20px 12px;
}
.path-card--transfer-to {
  background-color: rgba($color-primary, 0.10);
  box-shadow: 0 0 0 1px rgba($color-primary, 0.35);
  border-radius: 8px;
  padding: 20px 12px;
}
.path-card__letter--from { background-color: #e57373; }
.path-card__letter--to { background-color: $color-primary; }

.path-card__transfer-tags {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.transfer-tag {
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 20px;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: all 0.15s ease;
  white-space: nowrap;

  &--from {
    border-color: #e57373;
    color: #e57373;
    background: transparent;
    &:hover:not(:disabled) { background-color: #e57373; color: #fff; }
    &.transfer-tag--active { background-color: #e57373; color: #fff; pointer-events: none; }
  }
  &--to {
    border-color: $color-primary;
    color: $color-primary;
    background: transparent;
    &:hover:not(:disabled) { background-color: $color-primary; color: #fff; }
    &.transfer-tag--active { background-color: $color-primary; color: #fff; pointer-events: none; }
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.transfer-arrow {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}
.transfer-arrow__icon {
  font-size: 24px;
  font-weight: 700;
  color: $color-text-muted;
}

:global(.dark-mode) {
  .path-section__title { color: $color-text-dark; }
  .path-section__desc { color: $color-text-muted-dark; }
  .link { color: $color-primary-dark; }
  .mode-switcher { border-color: $color-border-dark; }
  .mode-switcher__btn { color: $color-text-muted-dark; &:hover:not(.mode-switcher__btn--active) { background-color: rgba($color-primary-dark, 0.08); } &--from.mode-switcher__btn--active { background-color: #e57373; color: #fff; } &--to.mode-switcher__btn--active { background-color: $color-primary-dark; color: $color-background-dark; } }
  .path-card {
    border-color: $color-border-dark;
    &:hover { background-color: rgba($color-primary-dark, 0.08); }
    &--selected { background-color: rgba($color-primary-dark, 0.14); box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba($color-primary-dark, 0.3); }
  }
  .path-card__letter { background-color: $color-primary-dark; color: $color-background-dark; &--selected { background-color: $color-primary-dark; } }
  .path-card__name { color: $color-text-dark; }
  .path-card__description { color: $color-text-muted-dark; }
  .path-card__alloc-label { color: $color-text-muted-dark; }
  .path-card__alloc-value { color: $color-text-dark; }
  .path-card__btn { border-color: $color-primary-dark; color: $color-primary-dark; &:hover:not(:disabled) { background-color: $color-primary-dark; color: $color-background-dark; } &--active { background-color: $color-primary-dark; color: $color-background-dark; } &--from { border-color: #e57373; background-color: #e57373; color: #fff; &:hover:not(:disabled) { background-color: #d32f2f; border-color: #d32f2f; color: #fff; } } &--to { border-color: $color-primary-dark; background-color: $color-primary-dark; color: $color-background-dark; } }
  .path-card--transfer-from { background-color: rgba(#e57373, 0.12); box-shadow: 0 0 0 1px rgba(#e57373, 0.4); }
  .path-card--transfer-to { background-color: rgba($color-primary-dark, 0.14); box-shadow: 0 0 0 1px rgba($color-primary-dark, 0.4); }
  .path-card__letter--from { background-color: #e57373; color: #fff; }
  .path-card__letter--to { background-color: $color-primary-dark; color: $color-background-dark; }
  .transfer-tag--from { border-color: #e57373; color: #e57373; &:hover:not(:disabled) { background-color: #e57373; color: #fff; } &.transfer-tag--active { background-color: #e57373; color: #fff; } }
  .transfer-tag--to { border-color: $color-primary-dark; color: $color-primary-dark; &:hover:not(:disabled) { background-color: $color-primary-dark; color: $color-background-dark; } &.transfer-tag--active { background-color: $color-primary-dark; color: $color-background-dark; } }
  .transfer-arrow__icon { color: $color-text-muted-dark; }
}

@media (max-width: 768px) {
  .path-section__title { text-align: center; }
  .path-card { display: flex; flex-wrap: wrap; justify-content: center; align-items: center; gap: 8px 12px; padding: 16px 0; }
  .path-card__badge { order: 1; flex-shrink: 0; width: 100%; display: flex; justify-content: center; }
  .path-card__name-col { order: 2; width: 100%; text-align: center; }
  .path-card__name { display: block; width: 100%; text-align: center; }
  .path-card__middle { order: 3; width: 100%; min-height: auto; display: flex; justify-content: center; }
  .path-card__description { text-align: center; }
  .path-card__allocations { justify-content: center; gap: 20px; }
  .path-card__action { order: 4; width: 100%; }
  .path-card__btn { width: 100%; }
  .path-card__transfer-btns { order: 4; width: 100%; justify-content: center; }
  .path-card__transfer-btns .path-card__btn { flex: 1; }
  .transfer-arrow { padding: 4px 0; }
}
</style>
